/**
 * What a business pays Teamna. The one source for every price on the website (pricing page, home
 * FAQ, JSON-LD offers, llms.txt, /pricing.md) and, later, for Desk billing (Roadmap, Wave 3).
 * Never type a price into a page: FSMA204Hub's share card kept an old price for months.
 *
 * Decided 2026-09-29: one plan, AED 275 a month for each business, no limit
 * on members, staff, groups or sessions. VAT is added where it applies. Teamna takes no fee from
 * the payments a business collects. There is no free plan and no free pilot (decided 2026-10-04):
 * every business starts with a 7-day free trial, no card needed, then pays the plan. Since
 * 2026-10-21 the plan has everything to run the business, and AI Assistant is an optional package
 * (`AI_ASSISTANT_PACKAGE`), so no page says "everything included".
 */
export const TEAMNA_PLAN = {
  id: "teamna",
  name: "Teamna",
  priceAed: 275,
  interval: "month",
  /** A new business starts with this many free days, no card needed (decided 2026-10-04). */
  trialDays: 7,
  /** The Stripe price this plan bills with. `stripe-billing setup` creates it with this lookup key. */
  stripePriceLookupKey: "teamna_monthly_aed_275",
  /** The Stripe product of the plan (`stripe-billing setup`). A Control Center discount applies to it only. */
  stripeProductId: "teamna_plan",
  /** The price excludes UAE VAT, which is added where it applies (service agreement, section 4). */
  vatIncluded: false,
  includes: [
    "No limit on members, staff, groups or sessions",
    "The web office for owners and the mobile app for staff, members and parents",
    "Fixed weekly slots and open sessions with packages of sessions",
    "Your cancellation and reschedule rules, applied by the app",
    "Card payments to your own account, with no fee from Teamna",
    "Cash and bank transfers recorded in the same place",
    "Reminders by push and WhatsApp",
    "Help from the Teamna team to set up your business",
  ],
} as const;

/**
 * AI Assistant, a package that a business adds to its plan (decided 2026-10-21): AED 149 a month,
 * VAT added where it applies. The owner or an admin adds it in Desk. It is a second line on the same
 * Stripe subscription, so it renews and pays with the plan. Teamna staff can also give it free in
 * Control Center, with an optional last day (`services/ai-assistant-access.ts`).
 */
/**
 * The branded app (white label), an add-on to the plan that is not live yet: the website says
 * "Coming soon" on every mention. AED 349 a month, VAT added where it applies, no setup fee and no
 * minimum term (decided 2026-10-21; replaces D8 in mobile-v2-and-white-label.md, setup AED 500 then
 * AED 200 a month). Apple and Google charge their own fees, in the business's own accounts. No
 * Stripe product until the first branded club signs.
 */
export const BRANDED_APP_ADDON = {
  id: "branded_app",
  name: "Branded app",
  priceAed: 349,
  interval: "month",
  vatIncluded: false,
} as const;

export const AI_ASSISTANT_PACKAGE = {
  id: "ai_assistant",
  name: "AI Assistant",
  priceAed: 149,
  interval: "month",
  /** The Stripe product of the package. `stripe-billing setup` creates it with this id. */
  stripeProductId: "teamna_ai_assistant",
  /** The Stripe price. A new price gets a new key with the same prefix (`isPackagePriceLookupKey`). */
  stripePriceLookupKey: "teamna_ai_assistant_monthly_aed_149",
  vatIncluded: false,
  includes: [
    "Suggestions each morning: links still unpaid, a session with no coach, a member who missed three in a row",
    "Answers from your own data, in English or Arabic",
    "Plans of changes and messages to families that wait for your Confirm",
  ],
} as const;

/** True for the price of a package, false for the plan. A subscription item with this key is not the plan. */
export function isPackagePriceLookupKey(key: string | null | undefined): boolean {
  return typeof key === "string" && key.startsWith("teamna_ai_assistant_");
}

/** "7-day free trial. No card needed." The trial line every page uses. */
export function formatTrial(plan: { trialDays: number } = TEAMNA_PLAN): string {
  return `${plan.trialDays}-day free trial. No card needed.`;
}

/** "AED 275 a month", the way every page says the price. Also for a package: `formatPlanPrice(AI_ASSISTANT_PACKAGE)`. */
export function formatPlanPrice(plan: { priceAed: number; interval: string } = TEAMNA_PLAN): string {
  return `AED ${plan.priceAed.toLocaleString("en-US")} a ${plan.interval}`;
}
